import { useState, useEffect, useMemo, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  TextField,
  FormControl,
  Chip,
  Snackbar,
  Alert,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import PatientService from "../../services/PatientService";
import JournalService from "../../services/JournalService";
import { CCMPatient } from "../../interfaces/CCMPatient";
import { UserContext } from "../../context/UserContext";
import BaselineVitalsDialog from "./BaselineVitalsDialog";
import { BaselineVitals } from "./BaselineVitalsDialog";
import EditPatientDialog from "./EditPatientDialog";
interface PatientPlan {
  patient_group: string;
  details: {
    measurement_type: string;
    interval_days: number;
    times_per_day: number;
    time_of_day_deadlines: string[];
  }[];
}

interface Props {
  patient: CCMPatient;
  patient_id: string;
  token: string;
  open: boolean;
  onClose: () => void;
  onStatusChange?: () => void;
  setOpen?: (open: boolean) => void;
  updatePatientList: (patient_id: string) => void;
}

const PatientPlanInfo = ({
  patient,
  token,
  open,
  setOpen,
  onClose,
  onStatusChange,
  updatePatientList,
}: Props) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [availablePlans, setAvailablePlans] = useState<PatientPlan[]>([]);
  const [patientEngagementPlan, setPatientEngagementPlan] = useState<PatientPlan[]>([]);
  const [selectedPlans, setSelectedPlans] = useState<string[]>([]);
  const [selectedType, setSelectedType] = useState<string>("");
  const [isComponentMounted, setIsComponentMounted] = useState(true);
  const [openTrainingConfirm, setOpenTrainingConfirm] = useState(false);
  const [latestTrainingDate, setLatestTrainingDate] = useState("");
  const [openBaselineVitalsDialog, setOpenBaselineVitalsDialog] = useState(false);
  const [editPatientOpen, setEditPatientOpen] = useState(false);
  const [editPatientLoading, setEditPatientLoading] = useState(false);
  const [editPatientError, setEditPatientError] = useState("");
  
  // Resend invitation states
  const [resendInvitationLoading, setResendInvitationLoading] = useState(false);
  const [resendInvitationMessage, setResendInvitationMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  // State for editable plan details  
  const [editablePlanDetails, setEditablePlanDetails] = useState<Record<string, {
    interval_days: number;
    deadlines: string[];
  }>>({});

  const [baselineVitals, setBaselineVitals] = useState<BaselineVitals>({
                spo2: patient.baseline_spo2 ? patient.baseline_spo2 : 0,
                weight: patient.baseline_weight || 0,
                bpSystolic: patient.baseline_systolic || 0,
                bpDiastolic: patient.baseline_diastolic || 0,
                fev1: patient.baseline_fev1 || 0,
                heartRate: patient.baseline_hr || 0,
                bloodGlucose: patient.baseline_glucose || 0,
                pef: 0,
                perfusionIndex: 0,
              });
  const userCtx = useContext(UserContext);
  const organization_id: string = userCtx?.user?.organization ?? "unknown";

  // Helper function to translate measurement type names for display
  const getMeasurementTypeDisplayName = (measurementType: string): string => {
    // Direct translations for common measurement types
    const translations: { [key: string]: string } = {
      "BloodPressure": t("BloodPressure"),
      "HeartRate": t("HeartRate"),
      "SpO2": t("SpO2"),
      "PeakFlow": t("PeakFlow"),
      "BloodGlucose": t("BloodGlucose"),
      "Weight": t("Weight"),
    };
    return translations[measurementType] || t(measurementType) || measurementType;
  };

  useEffect(() => {
    setIsComponentMounted(true);
    if (open && patient?.patient_id) {
      fetchEngagementPlan();
      fetchAvailablePlans();
    }
    return () => setIsComponentMounted(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fetchEngagementPlan = async () => {
    setLoading(true);
    //console.log("Fetching engagement plan for patient:", patient.patient_id);
    try {
      const data = await PatientService.fetchEngagementPlan(token, patient.patient_id);
      if (isComponentMounted && data) {
        //console.log("Raw engagement plan data from /api/v1/rpm/engagementplans/" + patient.patient_id + "/:", data);
        //console.log("Data type:", typeof data, "Is array:", Array.isArray(data));
        //if (Array.isArray(data)) {
        //  console.log("Plan details breakdown:");
        //  data.forEach((plan, index) => {
        //    console.log(`Plan ${index}:`, plan.patient_group, "Details:", plan.details);
        //  });
        //}
        if (data.error) {
          setLoading(false);
          return;
        }
        if (Array.isArray(data)) {
          setPatientEngagementPlan(data); // Store patient-specific plan data
          setSelectedPlans(data.map((plan: PatientPlan) => plan.patient_group));
          const selected = new Set<string>();
          data.forEach((plan: PatientPlan) => {
            (plan.details || []).forEach((d) => selected.add(d.measurement_type));
          });
          setBackendSelectedSet(selected);
        } else {
          //console.log("Single plan object - Patient group:", data.patient_group, "Details:", data.details);
          setPatientEngagementPlan([data]); // Store patient-specific plan data as array
          setSelectedPlans([data.patient_group]);
          const selected = new Set<string>();
          (data.details || []).forEach((d: any) => selected.add(d.measurement_type));
          setBackendSelectedSet(selected);
        }
      }
    } catch (error) {
      console.error("Failed to fetch engagement plan:", error);
    }
    setLoading(false);
  };

  const fetchAvailablePlans = async () => {
    try {
      const data = await PatientService.fetchEngagementPlans(token);
      if (isComponentMounted && data) {
        //console.log("Plans available are: ", data);
        setAvailablePlans(data);
      }
    } catch (error) {
      console.error("Failed to fetch available engagement plans:", error);
    }
  };

  useEffect(() => {
    if (selectedPlans.length === 1) {
      setSelectedType("RPM");
    } else if (selectedPlans.length > 1) {
      setSelectedType("CCM");
    } else {
      setSelectedType("");
    }
  }, [selectedPlans]);

  const mergedDetails = useMemo(() => {
    const measurementMap = new Map<string, PatientPlan["details"][0]>();
    
    // Use patient-specific engagement plan if available AND has details, otherwise fall back to available plans
    let plansToUse = patientEngagementPlan.length > 0 ? patientEngagementPlan : availablePlans;
    
    // Check which selected patient groups have patient-specific details
    const selectedPatientsWithDetails = selectedPlans.filter(planGroup =>
      patientEngagementPlan.some(plan => plan.patient_group === planGroup && plan.details.length > 0)
    );
    
    const selectedPatientsWithoutDetails = selectedPlans.filter(planGroup =>
      !patientEngagementPlan.some(plan => plan.patient_group === planGroup && plan.details.length > 0)
    );
    
    console.log("DEBUG: selectedPlans:", selectedPlans);
    console.log("DEBUG: selectedPatientsWithDetails:", selectedPatientsWithDetails);
    console.log("DEBUG: selectedPatientsWithoutDetails:", selectedPatientsWithoutDetails);
    
    // Use a hybrid approach: patient-specific plans where available, templates for the rest
    if (selectedPatientsWithoutDetails.length > 0) {
      // We need to merge patient-specific plans with template plans
      console.log("DEBUG: Using hybrid approach - mixing patient-specific and template plans");
      plansToUse = [
        ...patientEngagementPlan.filter(plan => selectedPatientsWithDetails.includes(plan.patient_group)),
        ...availablePlans.filter(plan => selectedPatientsWithoutDetails.includes(plan.patient_group))
      ];
    } else if (patientEngagementPlan.length > 0 && selectedPatientsWithDetails.length > 0) {
      // All selected plans have patient-specific data
      console.log("DEBUG: Using patient-specific plans only");
      plansToUse = patientEngagementPlan;
    } else {
      // Fall back to templates
      console.log("DEBUG: Using template plans only");
      plansToUse = availablePlans;
    }
    
    //console.log("Using plans for mergedDetails:", 
    //  plansToUse === patientEngagementPlan ? "patient-specific" : "templates", 
    //  plansToUse);
    
    plansToUse.forEach((plan) => {
      if (selectedPlans.includes(plan.patient_group)) {
        plan.details.forEach((detail) => {
          if (!measurementMap.has(detail.measurement_type)) {
            measurementMap.set(detail.measurement_type, detail);
          }
        });
      }
    });
    return Array.from(measurementMap.values());
  }, [selectedPlans, availablePlans, patientEngagementPlan]);

  // Track per-measurement selection (default to selected)
  const [measurementSelections, setMeasurementSelections] = useState<Record<string, boolean>>({});
  // Persisted selection from backend for the current patient (if any)
  const [backendSelectedSet, setBackendSelectedSet] = useState<Set<string> | null>(null);

  // Initialize measurement selections and editable plan details when mergedDetails changes
  useEffect(() => {
    //console.log("DEBUG: useEffect mergedDetails changed:", mergedDetails);
    if (!mergedDetails || mergedDetails.length === 0) return;
    
    // Only initialize if not already done, or if we have new measurements
    setMeasurementSelections((prev) => {
      const next: Record<string, boolean> = {};
      mergedDetails.forEach((d) => {
        // Use backend selection if available, otherwise default to true
        if (backendSelectedSet !== null) {
          next[d.measurement_type] = backendSelectedSet.has(d.measurement_type);
        } else {
          next[d.measurement_type] = prev.hasOwnProperty(d.measurement_type)
            ? prev[d.measurement_type]
            : true;
        }
      });
      //console.log("DEBUG: setting measurementSelections to:", next);
      return next;
    });
    
    // Initialize editable plan details
    setEditablePlanDetails((prev) => {
      const next: Record<string, { interval_days: number; deadlines: string[] }> = {};
      mergedDetails.forEach((d) => {
        next[d.measurement_type] = prev.hasOwnProperty(d.measurement_type)
          ? prev[d.measurement_type]
          : {
              interval_days: d.interval_days,
              deadlines: d.time_of_day_deadlines.length > 0 ? [...d.time_of_day_deadlines] : ['AM']
            };
      });
      return next;
    });
  }, [mergedDetails, backendSelectedSet]);

  const toggleMeasurement = (measurementType: string) => {
    setMeasurementSelections((prev) => ({
      ...prev,
      [measurementType]: !prev[measurementType],
    }));
  };

  // Reset selections when selectedPlans changes
  useEffect(() => {
    console.log("DEBUG: selectedPlans changed");
  }, [selectedPlans]);

  // Helper functions for editable plan details
  const updateIntervalDays = (measurementType: string, days: number) => {
    setEditablePlanDetails((prev) => ({
      ...prev,
      [measurementType]: {
        ...prev[measurementType],
        interval_days: days
      }
    }));
  };

  const updateDeadlines = (measurementType: string, deadlines: string[]) => {
    // Ensure at least one deadline is always selected
    const validDeadlines = deadlines.length === 0 ? ['AM'] : deadlines;
    
    setEditablePlanDetails((prev) => ({
      ...prev,
      [measurementType]: {
        ...prev[measurementType],
        deadlines: validDeadlines
      }
    }));
  };

  const getTimesPerDay = (deadlines: string[]): number => {
    return deadlines.length;
  };

  // Training registration functions
  const doRegisterTraining = async (override: boolean = false) => {
    const isoTimestamp = new Date().toISOString();
    const user_id: string = userCtx?.user?.username ?? "unknown";
    console.log(
      user_id,
      "registering training for patient:",
      patient.patient_id,
      "on date:",
      isoTimestamp
    );
    await JournalService.registerTraining(
      token,
      user_id,
      organization_id,
      patient.patient_id,
      isoTimestamp,
      override
    );
    onClose();
  };

  const handleRegisterTraining = async () => {
    const trainingJournals = await JournalService.getTrainingRecords(
      token,
      patient.patient_id,
      organization_id
    );
    console.log("trainingJournals: ", trainingJournals);
    const trainingRecords = trainingJournals?.records || [];
    if (trainingRecords.length > 0) {
      const latestRecord = trainingRecords[trainingRecords.length - 1];
      const lastDate = new Date(latestRecord);
      const formattedDate = format(new Date(lastDate), "yyyy-MM-dd");
      setLatestTrainingDate(formattedDate);
      setOpenTrainingConfirm(true);
      return;
    }
    doRegisterTraining();
  };

  const handleTrainingConfirmYes = () => {
    setOpenTrainingConfirm(false);
    doRegisterTraining(true);
  };

  const handleTrainingConfirmNo = () => {
    setOpenTrainingConfirm(false);
    console.log("User chose not to register another training record.");
  };

  // Plan assignment functions
  const handleAssignPlan = async () => {
    if (selectedPlans.length === 0 || !selectedType) return;
    try {
      const formattedDate = format(new Date(), "yyyy-MM-dd");
      // Collect selected measurements present in current details
      console.log("DEBUG: mergedDetails:", mergedDetails);
      console.log("DEBUG: measurementSelections:", measurementSelections);
      
      let selectedMeasurements = mergedDetails
        .map((d) => d.measurement_type)
        .filter((mt) => measurementSelections[mt]);
      
      console.log("DEBUG: selectedMeasurements after filtering:", selectedMeasurements);
      
      // Safety check: if no measurements are selected but we have available measurements, select all
      if (selectedMeasurements.length === 0 && mergedDetails.length > 0) {
        console.log("WARNING: No measurements selected, defaulting to all available measurements");
        selectedMeasurements = mergedDetails.map(d => d.measurement_type);
      }

      // Build custom_plan_details object organized by patient group
      const custom_plan_details: any = {};
      
      // Initialize structure for each selected plan
      selectedPlans.forEach(planGroup => {
        custom_plan_details[planGroup] = {};
      });

      // Populate with measurement details
      selectedMeasurements.forEach((measurementType) => {
        const originalDetail = mergedDetails.find(d => d.measurement_type === measurementType);
        const editableDetail = editablePlanDetails[measurementType];
        
        // Find which plan group this measurement belongs to
        const plansToSearch = patientEngagementPlan.length > 0 ? patientEngagementPlan : availablePlans;
        const planGroup = plansToSearch.find(plan => 
          selectedPlans.includes(plan.patient_group) && 
          plan.details.some(d => d.measurement_type === measurementType)
        )?.patient_group;

        if (planGroup) {
          custom_plan_details[planGroup][measurementType] = {
            interval_days: editableDetail?.interval_days || originalDetail?.interval_days || 1,
            times_per_day: getTimesPerDay(editableDetail?.deadlines || originalDetail?.time_of_day_deadlines || []),
            time_of_day_deadlines: editableDetail?.deadlines || originalDetail?.time_of_day_deadlines || []
          };
        }
      });

      console.log("Custom plan details:", custom_plan_details);

      // Use the new API endpoint with custom plan details
      await PatientService.assignEngagementPlanWithCustomDetails(
        token,
        patient.patient_id,
        selectedPlans,
        formattedDate,
        selectedMeasurements,
        custom_plan_details
      );
      const updatedPlan = await PatientService.fetchEngagementPlan(token, patient.patient_id);
      if (updatedPlan) {
        console.log("Updated plan after save:", updatedPlan);
        updatePatientList(patient.patient_id);
      }
    } catch (error) {
      console.error("Failed to assign engagement plan:", error);
    }
    onClose();
  };

  const deregisterPatient = async () => {
    //console.log("Deregistering patient: ", patient, "new disabled state:", patient.active);
    await PatientService.setPatientState(token, patient.patient_id, patient.active);
    //console.log("Deregister result: ", result);
    if (onStatusChange) {
      onStatusChange();
    }
    if (setOpen) {
      setOpen(false);
    }
  };

  // Handlers for Baseline Vitals dialog
  const handleOpenBaselineVitalsDialog = async () => {
    // Hydrate from backend GET baselines so values are current
    try {
      const data = await PatientService.getPatientBaselines(token, patient.patient_id);
      if (data?.measurements) {
        const m = data.measurements || {};
        setBaselineVitals({
          spo2: m.SpO2?.oxygen_saturation?.value ?? patient.baseline_spo2 ?? 0,
          weight: m.Weight?.weight?.value ?? patient.baseline_weight ?? 0,
          bpSystolic: m.BloodPressure?.systolic?.value ?? patient.baseline_systolic ?? 0,
          bpDiastolic: m.BloodPressure?.diastolic?.value ?? patient.baseline_diastolic ?? 0,
          fev1: m.PeakFlow?.fev1?.value ?? patient.baseline_fev1 ?? 0,
          heartRate: m.HeartRate?.heart_rate?.value ?? patient.baseline_hr ?? 0,
          bloodGlucose: m.BloodGlucose?.glucose?.value ?? patient.baseline_glucose ?? 0,
          // API doesn't expose PEF/perfusion baselines; keep zero
          pef: m.PeakFlow?.pef?.value ?? 0,
          perfusionIndex: m.SpO2?.perfusion_index?.value ?? 0,
        });
      }
    } catch (e) {
      // Ignore; will fall back to patient.* defaults
    }
    setOpenBaselineVitalsDialog(true);
  };

  const handleCloseBaselineVitalsDialog = () => {
    setOpenBaselineVitalsDialog(false);
  };

  const handleSaveBaselineVitals = async (vitals: any) => {
    // Build a baseline payload that only includes fields for selected measurements
    try {
      const selectedTypes = mergedDetails
        .filter((d) => measurementSelections[d.measurement_type] !== false)
        .map((d) => (d.measurement_type || '').toLowerCase());

      const include = (needles: string[]) =>
        needles.some((n) => selectedTypes.some((t) => t.includes(n)));

      const measurements: any = {};

      if (include(['spo2', 'oxygen'])) {
        measurements['SpO2'] = {
          oxygen_saturation: { unit: '%', value: Number(vitals.spo2) || 0 },
          perfusion_index: { unit: '%', value: Number(vitals.perfusionIndex) || 0 },
        };
      }
      if (include(['weight'])) {
        measurements['Weight'] = {
          weight: { unit: 'kg', value: Number(vitals.weight) || 0 },
        };
      }
      if (include(['blood', 'pressure']) || include(['bp'])) {
        measurements['BloodPressure'] = {
          systolic: { unit: 'mmHg', value: Number(vitals.bpSystolic) || 0 },
          diastolic: { unit: 'mmHg', value: Number(vitals.bpDiastolic) || 0 },
        };
      }
      if (include(['fev1']) || include(['peakflow']) || include(['peak'])) {
        measurements['PeakFlow'] = {
          fev1: { unit: 'L', value: Number(vitals.fev1) || 0 },
          pef: { unit: 'L/min', value: Number(vitals.pef) || 0 },
        };
      }
      if (include(['heart']) || selectedTypes.includes('hr')) {
        measurements['HeartRate'] = {
          heart_rate: { unit: 'bpm', value: Number(vitals.heartRate) || 0 },
        };
      }
      if (include(['glucose'])) {
        measurements['BloodGlucose'] = {
          glucose: { unit: 'mg/dL', value: Number(vitals.bloodGlucose) || 0 },
        };
      }

      await PatientService.setPatientBaselines(token, patient.patient_id, measurements);
    } catch (err) {
      console.error('Failed to save baseline vitals:', err);
    }

    setOpenBaselineVitalsDialog(false);
  };

  // Handler for resending invitation email
  const handleResendInvitation = async () => {
    setResendInvitationLoading(true);
    setResendInvitationMessage(null);

    try {
      await PatientService.resendInvitation(token, patient.patient_id);
      setResendInvitationMessage({
        type: 'success',
        text: t('Invitation email sent successfully!')
      });
    } catch (error) {
      console.error('Failed to resend invitation:', error);
      setResendInvitationMessage({
        type: 'error',
        text: t('Failed to resend invitation email. Please try again.')
      });
    } finally {
      setResendInvitationLoading(false);
    }
  };


  const handleCloseEditPatientDialog = () => {
    setEditPatientOpen(false);
    setEditPatientError('');
  };

  const handleSaveEditedPatient = async (updatedPatient: Partial<CCMPatient>) => {
    setEditPatientLoading(true);
    setEditPatientError('');

    try {
      if (!updatedPatient.patient_id) {
        throw new Error('Patient ID is required');
      }
      await PatientService.updatePatient(token, updatedPatient);
      // Refresh patient data
      window.location.reload(); // Simple approach - could be optimized
      setEditPatientOpen(false);
    } catch (error) {
      setEditPatientError('Failed to update patient information');
      console.error('Error updating patient:', error);
    } finally {
      setEditPatientLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>
          {t("Engagement Plan for Patient")}:{" "}
          {(patient.last_name && patient.first_name)
            ? `${patient.last_name}, ${patient.first_name}`
            : patient.patient_id}
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height={100}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Typography variant="h6">
                {t("Selected Patient Groups")}: {selectedPlans.join(", ") || t("None")}
              </Typography>
              {mergedDetails.length > 0 ? (
                <TableContainer component={Paper} sx={{ mt: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t("Include")}</TableCell>
                        <TableCell>{t("Measurement Type")}</TableCell>
                        <TableCell>{t("Interval Days")}</TableCell>
                        <TableCell>{t("Times Per Day")}</TableCell>
                        <TableCell>{t("Deadlines")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {mergedDetails.map((detail, index) => (
                        <TableRow key={index}>
                          <TableCell width={80}>
                            <Checkbox
                              color="primary"
                              checked={!!measurementSelections[detail.measurement_type]}
                              onChange={() => toggleMeasurement(detail.measurement_type)}
                            />
                          </TableCell>
                          <TableCell>{getMeasurementTypeDisplayName(detail.measurement_type)}</TableCell>
                          <TableCell>
                            {onStatusChange ? (
                              <TextField
                                type="number"
                                size="small"
                                value={editablePlanDetails[detail.measurement_type]?.interval_days || detail.interval_days}
                                onChange={(e) => updateIntervalDays(detail.measurement_type, parseInt(e.target.value) || 1)}
                                inputProps={{ min: 1, max: 30 }}
                                sx={{ width: 80 }}
                              />
                            ) : (
                              detail.interval_days
                            )}
                          </TableCell>
                          <TableCell>
                            {getTimesPerDay(editablePlanDetails[detail.measurement_type]?.deadlines || detail.time_of_day_deadlines)}
                          </TableCell>
                          <TableCell>
                            {onStatusChange ? (
                              <FormControl size="small" sx={{ minWidth: 120 }}>
                                <Select
                                  multiple
                                  displayEmpty
                                  value={editablePlanDetails[detail.measurement_type]?.deadlines || detail.time_of_day_deadlines}
                                  onChange={(e) => updateDeadlines(detail.measurement_type, typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                                  renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                      {selected.map((value) => (
                                        <Chip key={value} label={value} size="small" />
                                      ))}
                                    </Box>
                                  )}
                                >
                                  <MenuItem value="AM">
                                    <Checkbox 
                                      checked={(editablePlanDetails[detail.measurement_type]?.deadlines || detail.time_of_day_deadlines).indexOf("AM") > -1}
                                      size="small"
                                    />
                                    AM
                                  </MenuItem>
                                  <MenuItem value="PM">
                                    <Checkbox 
                                      checked={(editablePlanDetails[detail.measurement_type]?.deadlines || detail.time_of_day_deadlines).indexOf("PM") > -1}
                                      size="small"
                                    />
                                    PM
                                  </MenuItem>
                                </Select>
                              </FormControl>
                            ) : (
                              detail.time_of_day_deadlines.join(", ")
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography sx={{ mt: 2 }}>
                  {t("No measurements defined for the selected plan(s).")}
                </Typography>
              )}
            </>
          )}
          {/* First row: Plan selection, Monitoring type, and Baseline Vitals button */}
          <Box sx={{ 
            flexGrow: 1, 
            display: "flex", 
            alignItems: "center", 
            gap: 2,
            justifyContent: onStatusChange ? "flex-start" : "space-between"
          }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {onStatusChange && (
                <Select
                  multiple
                  value={selectedPlans}
                  onChange={(e) => {
                    const newPlans = typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value;
                    console.log("DEBUG: selectedPlans changing from:", selectedPlans, "to:", newPlans);
                    setSelectedPlans(newPlans);
                  }}
                  input={<OutlinedInput label={t("Select a plan")} />}
                  sx={{ minWidth: 200 }}
                  renderValue={(selected) => (selected as string[]).join(", ")}
                >
                  {availablePlans && availablePlans.length > 0 ? (
                    availablePlans.map((plan) => (
                      <MenuItem key={plan.patient_group} value={plan.patient_group}>
                        {plan.patient_group}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>{t("No plans available")}</MenuItem>
                  )}
                </Select>
              )}
              {onStatusChange && (
                <Select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  displayEmpty
                  sx={{ minWidth: 150 }}
                >
                  <MenuItem value="" disabled>
                    {t("Select Monitoring Type")}
                  </MenuItem>
                  <MenuItem value="RPM">RPM</MenuItem>
                  <MenuItem value="CCM">RPM & CCM</MenuItem>
                  <MenuItem value="Complex CCM">RPM & Complex CCM</MenuItem>
                </Select>
              )}
              <Button variant="contained" color="primary" onClick={handleOpenBaselineVitalsDialog}>
                {t("Register Baseline Vitals")}
              </Button>
              <Button variant="contained" color="primary" onClick={handleRegisterTraining}>
                {t("Register Training")}
              </Button>
            </Box>
            {/* Cancel and Save buttons when NOT opened from Patient Registry */}
            {!onStatusChange && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Button onClick={onClose}>{t("Cancel")}</Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAssignPlan}
                  disabled={selectedPlans.length === 0 || !selectedType}
                >
                  {t("SAVE")}
                </Button>
              </Box>
            )}
          </Box>
          </DialogContent>

          {/* DialogActions only shown when opened from Patient Registry */}
          {onStatusChange && (
            <DialogActions sx={{ justifyContent: "space-between", flexWrap: "wrap" }}>
              {/* Left side: Deactivate/Activate Patient, Edit Patient, and Resend Invitation */}
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button variant="contained" color="warning" onClick={deregisterPatient}>
                  {patient.active ? t("Deactivate Patient") : t("Activate Patient")}
                </Button>
                <Button variant="outlined" color="primary" onClick={() => setEditPatientOpen(true)}>
                  {t("Edit Patient")}
                </Button>
                <Button 
                  variant="outlined" 
                  color="secondary" 
                  onClick={handleResendInvitation}
                  disabled={resendInvitationLoading}
                >
                  {resendInvitationLoading ? t("Sending...") : t("Resend Invitation")}
                </Button>
              </Box>
              {/* Right side: Cancel and SAVE */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Button onClick={onClose}>{t("Cancel")}</Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAssignPlan}
                  disabled={selectedPlans.length === 0 || !selectedType}
                >
                  {t("SAVE")}
                </Button>
              </Box>
            </DialogActions>
          )}
      </Dialog>

      {/* Training Confirmation Dialog */}
      <Dialog
        open={openTrainingConfirm}
        onClose={handleTrainingConfirmNo}
        aria-labelledby="training-confirm-dialog-title"
      >
        <DialogTitle id="training-confirm-dialog-title">
          {t("Setup/training Code Already Exists")}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t("There is already registered a setup/training code for this patient on")}{" "}
            {latestTrainingDate}. {t("Do you want to register another one now?")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleTrainingConfirmNo}>{t("NO")}</Button>
          <Button variant="contained" onClick={handleTrainingConfirmYes}>
            {t("YES")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Baseline Vitals Dialog */}
      <BaselineVitalsDialog
        open={openBaselineVitalsDialog}
        initialVitals={baselineVitals}
        includedMeasurements={
          // Use mergedDetails filtered by current selections to decide which baseline fields to show
          mergedDetails
            .filter((d) => measurementSelections[d.measurement_type] !== false)
            .map((d) => d.measurement_type)
        }
        onClose={handleCloseBaselineVitalsDialog}
        onSave={handleSaveBaselineVitals}
      />

      {/* Edit Patient Dialog */}
      <EditPatientDialog
        open={editPatientOpen}
        patient={patient}
        onClose={handleCloseEditPatientDialog}
        onSave={handleSaveEditedPatient}
        isLoading={editPatientLoading}
        error={editPatientError}
      />

      {/* Resend Invitation Snackbar */}
      <Snackbar
        open={!!resendInvitationMessage}
        autoHideDuration={6000}
        onClose={() => setResendInvitationMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setResendInvitationMessage(null)} 
          severity={resendInvitationMessage?.type || 'info'}
        >
          {resendInvitationMessage?.text}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PatientPlanInfo;
